// The operator-facing text for a save that dropped something the editor cannot
// recreate.
//
// It lives in shared-types because BOTH clients need it and they cannot share
// anything else: `editor/app.js` is a hand-written bundle with no test harness and
// no module system, and `apps/fields-spa` cannot import from `@region13/fields-worker`
// (that package declares no `exports` and the SPA does not depend on it). Both do
// depend on this package, which is also where `FlaggedSubfield` is declared.
//
// Copy rules: no em dashes, no exclamation points.

import type { FlaggedSubfield } from './index'

/** Every literal fragment `flaggedNoticeText` can emit, KEYED. The editor bundle
 *  (`apps/fields/editor/app.js`) carries a hand-copy of this message because it has
 *  no module system, and `flaggedNotice.test.ts` asserts that copy contains every
 *  guardable fragment below. Add a fragment here and the guard immediately requires
 *  it there, which is the point: the guard cannot fall behind the message.
 *
 *  ⚠️ KEYED, not a positional array. It was a frozen array destructured by position,
 *  so reordering it silently reassigned all nine bindings while every test still
 *  passed — the fragments are interchangeable strings, so nothing downstream could
 *  notice the swap. */
export const FLAGGED_NOTICE_FRAGMENTS = Object.freeze({
  opening: 'Saved, but ',
  lostSuffix: ' lost settings the editor cannot restore: ',
  ageCapOpen: ' (age cap ',
  ageCapClose: 'U)',
  inleagueSuffix: ' (InLeague match)',
  separator: ' · ',
  closing: '. Re-set them before the next practice Generate.',
  singularNoun: 'field',
  pluralNoun: 'fields',
})

/** Fragment keys the app.js drift guard CANNOT meaningfully check. `field` and
 *  `fields` each occur ten times elsewhere in that bundle, so asserting their
 *  presence in it proves nothing and would stay green with the message deleted.
 *  They remain in the message; only the guard skips them. */
export const FLAGGED_NOTICE_UNGUARDABLE = Object.freeze(['singularNoun', 'pluralNoun'] as const)

/** One human sentence naming what a save deactivated while it still held an
 *  InLeague match or a measured practice age cap. Null when nothing was lost.
 *  Composed entirely from `FLAGGED_NOTICE_FRAGMENTS` so the two cannot drift. */
export function flaggedNoticeText(flagged: FlaggedSubfield[]): string | null {
  if (flagged.length === 0) return null
  const f = FLAGGED_NOTICE_FRAGMENTS
  const parts = flagged.map((x) =>
    x.reason === 'practice_max_age'
      ? `${x.label}${f.ageCapOpen}${x.practice_max_age}${f.ageCapClose}`
      : `${x.label}${f.inleagueSuffix}`,
  )
  const noun = flagged.length === 1 ? f.singularNoun : f.pluralNoun
  return `${f.opening}${flagged.length} ${noun}${f.lostSuffix}${parts.join(f.separator)}${f.closing}`
}
